/**
 * Pure rules behind the self-serve organisation-creation step and the access-ended page
 * (self-serve-org-signup task 7.10), kept dependency-free so they can be unit tested with
 * `node --test` — same split as addPersonRules.js / setupDisplay.js: the components stay
 * untested at interaction level and are covered by the dev click-throughs (task 8.3).
 * Run with: node --test src/features/allauth/pages/account/selfServeRules.test.js
 */

/**
 * The refusal codes the org-creation endpoint answers with (design decision 5.2), each
 * naming the specific problem and, where the user can act alone, how to resolve it. A code
 * the frontend doesn't recognise (network error, 500, anything else) falls through to the
 * generic entry — never silently swallowed, but never mistaken for a specific refusal either.
 */
export function refusalMessage({ code, existingOrgName } = {}) {
    switch (code) {
        case 'domain_claimed':
            return {
                title: 'An organisation already exists for your domain',
                body: existingOrgName
                    ? `${existingOrgName} has already been set up for your email domain. Ask its administrator for an invitation.`
                    : 'An organisation has already been set up for your email domain. Ask its administrator for an invitation.',
                showSupportLink: true
            }
        case 'consumer_domain':
            return {
                title: 'A work or institutional email address is needed',
                body: "Organisations are claimed by the domain of a verified work or institutional email address, and this looks like a personal email provider. Sign up again with that address, or get in touch if you don't have one.",
                showSupportLink: true
            }
        case 'already_has_org':
            return {
                title: 'You already belong to an organisation',
                body: 'Your account is already attached to an organisation, so a new one cannot be created.',
                showSupportLink: true
            }
        case 'not_verified':
            return {
                title: 'Verify your email first',
                body: 'Please verify your email address before creating an organisation.',
                showSupportLink: false
            }
        case 'not_eligible':
            // Defensive: OrgAccessGuard already keeps partner staff away from this step
            // (they have no home org by design and administer a portfolio instead), so
            // the endpoint shouldn't be reachable this way — but it answers with its own
            // code rather than pretending the other four cover everyone, so this exists.
            return {
                title: "Partner accounts don't create organisations this way",
                body: "Your account administers a partner portfolio rather than belonging to a single organisation, so this signup step isn't for you. Provision a new organisation for a customer from the partner console instead.",
                showSupportLink: false
            }
        case 'signup_disabled': {
            // Defensive: CreateOrganisation checks the flag before rendering the form at
            // all, so this shouldn't normally be reachable — but someone can be mid-flow
            // (form open, not yet submitted) when the flag flips off underneath them, and
            // the server answers with its own code rather than a generic failure.
            const { title, body } = signupDisabledMessage()
            return { title, body, showSupportLink: true }
        }
        default:
            return {
                title: 'Could not create your organisation',
                body: 'Something went wrong. Please try again, or get in touch if it keeps happening.',
                showSupportLink: true
            }
    }
}

/** Pull `{code, existingOrgName}` out of an axios error, tolerating a missing/malformed body. */
export function readRefusal(error) {
    const data = error?.response?.data
    return {
        code: typeof data?.code === 'string' ? data.code : undefined,
        existingOrgName:
            typeof data?.existingOrgName === 'string'
                ? data.existingOrgName
                : null
    }
}

// Title-cased display roles (user.roles, not the snake_case user.roleNames — see
// frontend/CLAUDE.md's Role-Based Access section) that may act on billing.
const BILLING_ADMIN_ROLES = ['Admin Role', 'Site Admin Role']

/**
 * Whether the access-ended page should offer the billing link. An admin can act on it; a
 * member cannot, and offering a button they cannot use is worse than offering none (design
 * goal — see the self-serve-signup-ui spec's "Non-admin is told who to ask" scenario).
 * Takes the plain `roles` array rather than a full user object so it stays dependency-free.
 */
export function showBillingLink(roles) {
    const owned = new Set(roles ?? [])
    return BILLING_ADMIN_ROLES.some((role) => owned.has(role))
}

/**
 * Copy for the page shown when `activeOrg.is_billing_active === false`. Deliberately NOT
 * trial-specific: that flag goes false for several reasons OrgAccessGuard can't tell apart —
 * a lapsed trial, a failed card (past_due), a cancelled subscription, or a site admin's
 * manual override (`billing/site_admin_views.py`) — and `BILLING_SUMMARY`, which could
 * disambiguate, is gated to org admins (`billing_self_service`), so an ordinary member would
 * get a 403 from it. A branch that only resolves for admins and silently falls back for
 * everyone else is worse than no branch, so the wording stays accurate for all four causes
 * rather than guessing "trial" and being wrong for a paying customer with a declined card.
 * Same admin/non-admin split as showBillingLink, same CTA regardless of cause.
 */
export function accessEndedCopy(canPay) {
    const body = canPay
        ? "Your organisation's subscription isn't active, so this workspace is paused. Subscribe or update your billing details to restore access. Nothing has been lost."
        : "Your organisation's subscription isn't active, so this workspace is paused. Ask your organisation's administrator to sort out billing to restore access. Nothing has been lost."

    return {
        title: 'Access has ended',
        body,
        ctaLabel: canPay ? 'Manage billing' : null
    }
}

/**
 * Whether the public signup front door (the register/register-success/resend-verification
 * routes, and the organisation-creation step) should be reachable. Takes the raw flag value
 * — `brand.self_serve_signup_enabled` from `GET /api/branding/` — rather than the whole
 * brand object, so it stays a plain boolean check with no shape assumptions. Anything other
 * than the literal `true` (missing, `undefined`, a stale cached brand, an older backend that
 * doesn't send the field, a branding fetch that hasn't resolved yet) reads as disabled — the
 * safe failure for a public front door is closed, not open.
 */
export function isSignupEnabled(flag) {
    return flag === true
}

/** Copy for the "not currently open" state — shared between the org-creation step (shown
 * proactively before the form) and the `signup_disabled` refusal (shown defensively if the
 * flag flips mid-flow), so the two never drift into saying different things. */
export function signupDisabledMessage() {
    return {
        title: 'Creating an organisation is not currently open',
        body: "Self-serve organisation creation isn't available right now. If you were invited to join an existing organisation, check your email for that invitation. If you think this is a mistake, get in touch."
    }
}
